import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// ─── Markdown Renderer ─────────────────────────────────────────

function unescapeContent(content: string): string {
  // Handle double-serialized JSON strings
  if (typeof content === 'string' && content.startsWith('"')) {
    try { return JSON.parse(content); } catch { /* fall through */ }
  }
  return content;
}

export const MarkdownContent: React.FC<{ content: string }> = ({ content }) => {
  const text = unescapeContent(content);
  return (
    <div className="markdown-content" style={{ fontSize: 'inherit', lineHeight: 'inherit', color: 'inherit', minWidth: 0 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // A link in a chat message must never replace the chat itself (canary
          // 2026-10-03: opening a pull request link killed the session).
          a(props) {
            const { node, children, ...rest } = props as any;
            return <a {...rest} target="_blank" rel="noopener noreferrer">{children}</a>;
          },
          code(props) {
            const { className, children, node, ...rest } = props as any;
            const match = /language-(\w+)/.exec(className || '');
            const codeStr = String(children).replace(/\n$/, '');
            const isBlock = match || codeStr.includes('\n');
            if (isBlock) {
              return (
                <SyntaxHighlighter
                  style={oneDark}
                  language={match?.[1] || 'text'}
                  PreTag="div"
                  customStyle={{
                    margin: '8px 0',
                    borderRadius: '6px',
                    fontSize: '12px',
                    border: '1px solid var(--border)',
                    maxWidth: '100%',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {codeStr}
                </SyntaxHighlighter>
              );
            }
            return (
              <code
                className={className}
                style={{
                  background: 'var(--surface3)',
                  padding: '1px 5px',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontFamily: 'var(--font-mono)',
                }}
                {...rest}
              >
                {children}
              </code>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};
